import { ChevronDown, Search } from "lucide-react";
import { type KeyboardEvent, useEffect, useId, useMemo, useRef, useState } from "react";

import { controlClass, controlHeight } from "@/components/ui/field";
import { popoverStyle, usePopoverPosition } from "@/hooks/use-popover-position";
import {
  DEFAULT_COUNTRY,
  findCountry,
  formatPhone,
  localPart,
  namedCountries,
  retypePhone,
  type Country,
} from "@/constants/countries";
import { useLocale, useTranslation } from "@/store/locale";
import { cn } from "@/utils/utils";

/** A number and the flag it was entered under. The pair the API stores. */
export interface PhoneValue {
  /** ISO 3166-1 alpha-2, lower-cased. */
  country: string;
  /** The number whole, dialling code and leading plus included. */
  phone: string;
}

/**
 * Whether anything was actually entered.
 *
 * Choosing a country fills the box with its dialling code, so "+63" is the
 * state of a row nobody has typed in rather than a number — it would be stored
 * as a contact who cannot be rung.
 */
export function hasNumber(value: PhoneValue): boolean {
  const country = findCountry(value.country);
  return localPart(value.phone, country).replace(/\D/g, "").length > 0;
}

/** A fresh field for a country: just its dialling code, ready to be typed after. */
export const emptyPhone = (country = DEFAULT_COUNTRY): PhoneValue => ({
  country,
  phone: `+${findCountry(country)?.dial ?? ""}`,
});

/**
 * A telephone number: the flag it belongs to, then the number itself.
 *
 * The country is a picker rather than a prefix somebody types, because the
 * flag is the part that has to be read back at a glance and "+63" is not one.
 * It is also only ever a hint — the number is stored whole, dialling code
 * included, so nothing downstream has to join two halves back together and
 * guess which country a bare "+1" was.
 *
 * Changing the country rewrites the dialling code in place and leaves what was
 * typed after it, so picking the wrong flag first is a correction rather than
 * a retype.
 */
export function PhoneInput({
  id,
  value,
  onChange,
  placeholder,
  disabled = false,
  invalid = false,
  countryLabel,
  className,
  "aria-label": ariaLabel,
}: {
  id?: string | undefined;
  value: PhoneValue;
  onChange: (value: PhoneValue) => void;
  placeholder?: string | undefined;
  disabled?: boolean;
  invalid?: boolean | undefined;
  /** Names the flag button for a screen reader; the visible label is the field's. */
  countryLabel?: string | undefined;
  className?: string | undefined;
  "aria-label"?: string | undefined;
}) {
  const t = useTranslation();
  const country = findCountry(value.country) ?? findCountry(DEFAULT_COUNTRY);

  const pickCountry = (next: Country) => {
    if (next.code === value.country) return;
    // The number goes with the country, rather than being carried across to a
    // new dialling code. A ten-digit US number is not a Philippine number with
    // a different prefix — it is not a Philippine number at all, and carrying
    // the digits over produces something that looks entered and dials nowhere.
    // Emptied back to the bare dialling code, so the box says what to type next.
    onChange({ country: next.code, phone: `+${next.dial}` });
  };

  return (
    <div className={cn("flex items-start gap-2", className)}>
      <CountryPicker
        value={country?.code ?? DEFAULT_COUNTRY}
        onChange={pickCountry}
        disabled={disabled}
        invalid={invalid}
        label={countryLabel ?? t("profile.countryCode")}
      />
      {/* Shown spaced, stored plain. The box reads `+63 917 123 4567`; the row
          keeps `+639171234567`, so nothing downstream has to strip somebody's
          punctuation back out before dialling or comparing. */}
      <input
        id={id}
        type="tel"
        inputMode="tel"
        autoComplete="tel"
        aria-label={ariaLabel}
        aria-invalid={invalid || undefined}
        disabled={disabled}
        value={formatPhone(country, value.phone)}
        placeholder={placeholder}
        onChange={(event) =>
          onChange({ ...value, phone: retypePhone(event.target.value, country) })
        }
        className={cn(controlClass, controlHeight.md, "min-w-0 flex-1")}
      />
    </div>
  );
}

/**
 * The flag, and a list of every country behind it.
 *
 * Searched, because there are 250 of them and no arrow key gets to Zimbabwe.
 * The search matches the name, the code and the dialling code, so "63", "ph"
 * and "Philippines" all find the same row — people reach for whichever of the
 * three they happen to know.
 *
 * Built like `OptionSelect`: a fixed popover placed off the trigger's rect so a
 * modal's scrolling body cannot clip it, focus kept on one element, and the
 * active row named by `aria-activedescendant`.
 */
function CountryPicker({
  value,
  onChange,
  label,
  disabled,
  invalid,
}: {
  /** ISO 3166-1 alpha-2. The named row is resolved from the list below, which
   *  is where the display name lives. */
  value: string;
  onChange: (country: Country) => void;
  label: string;
  disabled: boolean;
  invalid: boolean | undefined;
}) {
  const locale = useLocale();
  const t = useTranslation();
  const root = useRef<HTMLDivElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const search = useRef<HTMLInputElement>(null);
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const box = usePopoverPosition(root, open);

  const all = useMemo(() => namedCountries(locale), [locale]);
  const chosen = useMemo(() => all.find((country) => country.code === value), [all, value]);
  const rows = useMemo(() => {
    const needle = query.trim().toLowerCase().replace(/^\+/, "");
    if (!needle) return all;
    return all.filter(
      (country) =>
        country.name.toLowerCase().includes(needle) ||
        country.code.includes(needle) ||
        country.dial.startsWith(needle),
    );
  }, [all, query]);

  useEffect(() => {
    if (!open) return;
    const away = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!root.current?.contains(target) && !menu.current?.contains(target)) setOpen(false);
    };
    document.addEventListener("pointerdown", away);
    return () => document.removeEventListener("pointerdown", away);
  }, [open]);

  // The search box takes the focus on the way in, so the list can be narrowed
  // by typing without a second gesture to get there.
  useEffect(() => {
    if (open) search.current?.focus();
  }, [open]);

  const show = (index: number) => {
    setActive(index);
    menu.current
      ?.querySelectorAll("li > button")
      [index]?.scrollIntoView?.({ block: "nearest" });
  };

  const take = (country: Country) => {
    onChange(country);
    setOpen(false);
    setQuery("");
  };

  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    const { key } = event;
    if (key === "ArrowDown" || key === "ArrowUp") {
      event.preventDefault();
      if (rows.length === 0) return;
      const step = key === "ArrowDown" ? 1 : -1;
      show((active + step + rows.length) % rows.length);
      return;
    }
    if (key === "Enter") {
      // Inside a form, and the list is what Enter is for while it is open —
      // without this the half-filled form behind it submits.
      event.preventDefault();
      const country = rows[active];
      if (country) take(country);
      return;
    }
    if (key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
      return;
    }
    if (key === "Tab") setOpen(false);
  };

  const openList = () => {
    setQuery("");
    setActive(
      Math.max(
        0,
        all.findIndex((country) => country.code === value),
      ),
    );
    setOpen(true);
  };

  return (
    <div ref={root} className="relative shrink-0">
      <button
        type="button"
        role="combobox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-haspopup="listbox"
        aria-label={chosen ? `${label}: ${chosen.name} +${chosen.dial}` : label}
        aria-invalid={invalid || undefined}
        disabled={disabled}
        onClick={() => (open ? setOpen(false) : openList())}
        onKeyDown={(event) => {
          if (open) return;
          if (event.key === "ArrowDown" || event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            openList();
          }
        }}
        className={cn(
          controlClass,
          controlHeight.md,
          "flex w-[4.25rem] items-center justify-center gap-1 px-2",
          open && "border-accent ring-[3px] ring-[var(--accent-ring)]",
        )}
      >
        {/* The flag is the whole label, and `aria-label` above says it in words.
            Read out as an emoji name it would be "flag: Philippines" in the
            middle of a form, which is noise on top of the label. */}
        <span aria-hidden className="text-[1.15rem] leading-none">
          {chosen?.flag ?? "🏳"}
        </span>
        <ChevronDown className="text-fg-subtle size-3.5 shrink-0" aria-hidden />
      </button>

      {open ? (
        <div
          ref={menu}
          style={popoverStyle(box, 300)}
          className="border-line bg-surface-raised animate-sheet fixed z-50 origin-top overflow-hidden rounded-xl border shadow-[var(--shadow-float)]"
        >
          <div className="hairline-b relative p-1.5">
            <Search
              aria-hidden
              className="text-fg-subtle pointer-events-none absolute top-1/2 left-4 size-4 -translate-y-1/2"
            />
            <input
              ref={search}
              type="text"
              role="combobox"
              aria-expanded
              aria-controls={listId}
              aria-activedescendant={`${listId}-${active}`}
              aria-label={label}
              value={query}
              placeholder={t("profile.countrySearch")}
              onChange={(event) => {
                setQuery(event.target.value);
                setActive(0);
              }}
              onKeyDown={onKeyDown}
              className={cn(controlClass, controlHeight.sm, "pl-8")}
            />
          </div>
          <ul id={listId} role="listbox" className="max-h-64 overflow-y-auto p-1">
            {rows.map((country, index) => (
              <li key={country.code} role="option" aria-selected={country.code === value}>
                <button
                  type="button"
                  id={`${listId}-${index}`}
                  tabIndex={-1}
                  onMouseEnter={() => setActive(index)}
                  onClick={() => take(country)}
                  className={cn(
                    "text-callout flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors",
                    country.code === value
                      ? "bg-accent-soft text-accent font-medium"
                      : index === active
                        ? "bg-surface-inset text-fg"
                        : "text-fg",
                  )}
                >
                  <span aria-hidden className="text-[1.15rem] leading-none">
                    {country.flag}
                  </span>
                  <span className="min-w-0 flex-1 truncate">{country.name}</span>{" "}
                  <span className="text-fg-subtle text-subhead tabular-nums">
                    +{country.dial}
                  </span>
                </button>
              </li>
            ))}
            {rows.length === 0 ? (
              <li className="text-fg-subtle text-callout px-2.5 py-3 text-center">
                {t("profile.countryNone")}
              </li>
            ) : null}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
